import * as React from 'react';
import { JsonLdResult, getLocalName } from 'vet-bones/utils';

import { useGenerateNewIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useGenerateNewIri';
import { addAttributeItemToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptAttributeForClass';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { addRelationshipData } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addModelConceptRelationship';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import {
  OWL_CLASS_IRI,
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_DOMAIN_IRI,
  RDFS_LABEL_IRI,
  RDFS_RANGE_IRI,
  SCHEMA_ORG_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_RANGE_INCLUDES_IRI,
  XSD_STRING_IRI,
} from 'src/templates/queries/iris';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { arrayify } from 'src/utils/jsonld/arrayify';
import { getMultiple, getSingle } from 'src/utils/jsonld/utils';

export const useCreateModel = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const generateNewIri = useGenerateNewIri();

  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );

  return React.useCallback(
    async (jsonLdModel: JsonLdResult[] | null): Promise<string[]> => {
      if (!diagram || !jsonLdModel) {
        return [];
      }

      const projectModelIds = targetModelId ? [targetModelId] : [];
      const createdClassIris: string[] = [];

      await commitDiagramModel(
        diagram,
        (model) => {
          const classes: Record<string, { iri: string; label: string }> = {};
          const attributes: Record<
            string,
            { iri: string; label: string; domains: string[]; range?: string }
          > = {};
          const relationships: Record<
            string,
            { iri: string; label: string; domains: string[]; ranges: string[] }
          > = {};

          jsonLdModel.forEach((result) => {
            const iri = getSingle<string>(result, '@id');
            if (!iri) {
              return;
            }

            const type = arrayify(result['@type']);
            const label =
              getSingle<string>(result[RDFS_LABEL_IRI], '@value') ||
              getLocalName(iri);

            if (type.includes(OWL_CLASS_IRI)) {
              classes[iri] = { iri, label };
            } else if (type.includes(OWL_DATATYPE_PROPERTY_IRI)) {
              const domains = [
                ...getMultiple<string>(result[RDFS_DOMAIN_IRI], '@id'),
                ...getMultiple<string>(
                  result[SCHEMA_ORG_DOMAIN_INCLUDES_IRI],
                  '@id'
                ),
              ];
              const ranges = [
                ...getMultiple<string>(result[RDFS_RANGE_IRI], '@id'),
                ...getMultiple<string>(
                  result[SCHEMA_ORG_RANGE_INCLUDES_IRI],
                  '@id'
                ),
              ];

              attributes[iri] = {
                iri,
                label,
                domains,
                range: ranges[0] || XSD_STRING_IRI,
              };
            } else if (type.includes(OWL_OBJECT_PROPERTY_IRI)) {
              const domains = [
                ...getMultiple<string>(result[RDFS_DOMAIN_IRI], '@id'),
                ...getMultiple<string>(
                  result[SCHEMA_ORG_DOMAIN_INCLUDES_IRI],
                  '@id'
                ),
              ];
              const ranges = [
                ...getMultiple<string>(result[RDFS_RANGE_IRI], '@id'),
                ...getMultiple<string>(
                  result[SCHEMA_ORG_RANGE_INCLUDES_IRI],
                  '@id'
                ),
              ];

              relationships[iri] = {
                iri,
                label,
                domains,
                ranges,
              };
            }
          });

          const iriMapping: Record<string, string> = {};
          const classIris = new Set<string>();

          Object.values(classes).forEach(({ iri, label }) => {
            const newIri = generateNewIri(iri);
            iriMapping[iri] = newIri;
            classIris.add(newIri);
            createdClassIris.push(newIri);

            addModelConceptClass({
              iri: newIri,
              label,
              projectModelIds,
            })(model);
          });

          Object.values(attributes).forEach(
            ({ iri, label, domains, range }) => {
              const newIri = generateNewIri(iri, classIris);
              iriMapping[iri] = newIri;

              domains.forEach((domainIri) => {
                const mappedDomainIri = iriMapping[domainIri] || domainIri;
                addAttributeItemToClass(mappedDomainIri, {
                  text: label,
                  value: newIri,
                  isNewlyCreated: true,
                  ranges: [
                    {
                      text: range || XSD_STRING_IRI,
                      value: range || XSD_STRING_IRI,
                    },
                  ],
                })(model);
              });
            }
          );

          const classAndAttributeIris = new Set([
            ...classIris,
            ...Object.keys(attributes).map((iri) => iriMapping[iri]),
          ]);

          Object.values(relationships).forEach(
            ({ iri, label, domains, ranges }) => {
              const newIri = generateNewIri(iri, classAndAttributeIris);
              iriMapping[iri] = newIri;

              domains.forEach((domainIri) => {
                ranges.forEach((rangeIri) => {
                  if (domainIri === rangeIri) {
                    return;
                  }

                  const mappedDomainIri = iriMapping[domainIri] || domainIri;
                  const mappedRangeIri = iriMapping[rangeIri] || rangeIri;

                  addRelationshipData({
                    fromIri: mappedDomainIri,
                    toIri: mappedRangeIri,
                    value: newIri,
                    model,
                  });
                });
              });

              setLabelForIri(newIri, label)(model);
            }
          );
        },
        { layout: DiagramModelCommitLayout.FULL }
      );

      return createdClassIris;
    },
    [diagram, generateNewIri, targetModelId]
  );
};
